/**
 * CualificacionMatrix - Matriz de cualificación de niveles éticos
 * Features:
 * - Tabla de niveles con rangos de puntuación
 * - Indicador del nivel actual
 * - Descripción de cada nivel
 */
import { CheckCircle, AlertTriangle, AlertCircle, XCircle } from 'lucide-react';

const NIVELES = [
  {
    nombre: 'Alto',
    rango: '10-12 puntos',
    porcentaje: '≥83%',
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    textColor: 'text-green-800',
    icon: CheckCircle,
    descripcion: 'Nivel ético excelente. La organización demuestra un compromiso sólido con prácticas éticas.',
  },
  {
    nombre: 'Medio',
    rango: '7-9.5 puntos',
    porcentaje: '58-79%',
    color: 'yellow',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-500',
    textColor: 'text-yellow-800',
    icon: AlertTriangle,
    descripcion: 'Nivel ético aceptable. Existen áreas de mejora que requieren atención.',
  },
  {
    nombre: 'Bajo',
    rango: '4-6.5 puntos',
    porcentaje: '33-54%',
    color: 'orange',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-800',
    icon: AlertCircle,
    descripcion: 'Nivel ético bajo. Se requieren acciones correctivas significativas.',
  },
  {
    nombre: 'Crítico',
    rango: '0-3.5 puntos',
    porcentaje: '<29%',
    color: 'red',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    textColor: 'text-red-800',
    icon: XCircle,
    descripcion: 'Nivel ético crítico. Se requiere intervención urgente e implementación de medidas correctivas inmediatas.',
  },
];

const CualificacionMatrix = ({ data }) => {
  const { puntuacion_promedio, nivel_etico, distribucion } = data;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900">
          Matriz de Cualificación Ética
        </h4>
        <p className="text-sm text-gray-500 mt-1">
          Niveles de evaluación basados en la puntuación obtenida
        </p>
      </div>

      {/* Información de puntuación */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Puntuación Promedio</p>
            <p className="text-2xl font-bold text-gray-900">
              {puntuacion_promedio?.toFixed(1) || 0} / 12
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Nivel Ético Alcanzado</p>
            <p className="text-2xl font-bold text-primary-600">
              {nivel_etico || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabla de niveles */}
      <div className="space-y-3">
        {NIVELES.map((nivel) => {
          const Icon = nivel.icon;
          const esNivelActual = nivel.nombre === nivel_etico;
          const cantidad = distribucion?.[nivel.nombre] || 0;

          return (
            <div
              key={nivel.nombre}
              className={`rounded-lg border-2 transition-all ${
                esNivelActual
                  ? `${nivel.borderColor} ${nivel.bgColor}`
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  {/* Información del nivel */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon
                        className={`h-6 w-6 ${
                          esNivelActual
                            ? nivel.color === 'green'
                              ? 'text-green-600'
                              : nivel.color === 'yellow'
                              ? 'text-yellow-600'
                              : nivel.color === 'orange'
                              ? 'text-orange-600'
                              : 'text-red-600'
                            : 'text-gray-400'
                        }`}
                      />
                      <div>
                        <h5
                          className={`font-semibold ${
                            esNivelActual ? nivel.textColor : 'text-gray-900'
                          }`}
                        >
                          {nivel.nombre}
                        </h5>
                        <p className="text-sm text-gray-500">
                          {nivel.rango} ({nivel.porcentaje})
                        </p>
                      </div>
                    </div>
                    <p
                      className={`text-sm ${
                        esNivelActual ? 'text-gray-700' : 'text-gray-600'
                      }`}
                    >
                      {nivel.descripcion}
                    </p>
                  </div>

                  {/* Badge de nivel actual o cantidad */}
                  <div className="ml-4">
                    {esNivelActual ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        Nivel Actual
                      </span>
                    ) : cantidad > 0 ? (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-700">
                          {cantidad}
                        </p>
                        <p className="text-xs text-gray-500">
                          participante{cantidad !== 1 ? 's' : ''}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Leyenda de cálculo */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="font-semibold text-blue-900 mb-2">
          Metodología de Cálculo
        </h5>
        <div className="text-sm text-blue-800 space-y-1">
          <p>• Respuesta "Sí": +1 punto</p>
          <p>• Respuesta "Parcialmente": +0.5 puntos</p>
          <p>• Respuesta "No": 0 puntos</p>
          <p className="mt-2 font-medium">
            Total posible: 12 puntos (12 preguntas)
          </p>
        </div>
      </div>
    </div>
  );
};

export default CualificacionMatrix;
